// ListeningAura.js — the Siri-style "I'm listening" signal: a blue-green
// light sweeps around the whole viewport edge whenever the agent is
// actively listening (or speaking back). Portaled to <body> so it frames
// the entire screen regardless of where the panel is, and pointer-events
// are off so it never intercepts a click. All motion is in CSS
// (LIVE_KEYFRAMES: cplAuraSpin / cplAuraBreath); this just gates mount +
// the fade in/out.
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useComprehendLive } from 'agent/useComprehendLive';
import { agentVisiblyEngaged } from 'agent/liveStore';

const EXIT_MS = 1000; // matches .cpl-aura.out

const ListeningAura = () => {
  const { state } = useComprehendLive();
  // Glow while the agent is engaged: explicitly listening, speaking back, OR
  // working an overheard ambient instruction (agentActing). Shares a home with
  // agentHoldsAudio (the ambient-suppression predicate) so the glow and the
  // brain standing down can't drift apart again.
  const active = agentVisiblyEngaged(state);

  // Keep mounted through the fade-out, then unmount.
  const [rendered, setRendered] = useState(false);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (active) {
      setRendered(true);
      setLeaving(false);
      return undefined;
    }
    if (rendered) {
      setLeaving(true);
      const t = setTimeout(() => setRendered(false), EXIT_MS);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [active, rendered]);

  if (!rendered) return null;

  return createPortal(
    <div className={`cpl-aura ${leaving ? 'out' : 'in'}`} aria-hidden="true">
      <div className="cpl-aura-glow" />
      <div className="cpl-aura-bloom" />
      <div className="cpl-aura-ring" />
    </div>,
    document.body,
  );
};

export default ListeningAura;
